import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import Page from 'src/components/common/Page';
import Panel from 'src/components/common/Panel';
import Button from 'src/components/controls/forms/redesign/FormButton';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import RHFSelect from 'src/components/controls/tempRHFControls/RHFSelect';
import { IRootState } from 'src/reducers';
import PersonnelSearch from '../components/PersonnelSearch';
import useFetch from 'use-http';
import LoadingSpinner from 'src/components/common/LoadingSpinner';
import DialogList from 'src/dialogs';
import actions from 'src/actions';
import DefaultBillHistoryDialog from '../dialogs/pay/DefaultBillHistoryDialog';
import PersonnelNavBar from '../components/PersonnelNavBar';
import PersonnelAdvancedQuickFind from '../dialogs/PersonnelAdvancedQuickFindDialog';
import PersonnelToast from '../components/PersonnelToast';
import LandmanBillInfo from 'src/models/personnel/LandmanBillInfo';
import UpdateBillHistoryDialog from '../dialogs/pay/UpdateDefaultBillPayDialog';
import TSDILandman from 'src/models/personnel/TSDILandman';
import LandmanBillExceptionHistory from 'src/models/personnel/LandmanBillExceptionHistory';
import LandmanBillException from 'src/models/personnel/LandmanBillException';
import BillExceptionHistoryDialog from '../dialogs/pay/BillingExceptionHistoryDialog';

const BillingPage = () => {
    const { register, errors, setValue } = useForm();
    const { get, response } = useFetch();
    const [toast, setToast] = useState<{ message: string; color?: string, persistent?: boolean; } | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [defaultBillHistory, setDefaultBillHistory] = useState<Array<LandmanBillInfo>>(undefined); // The whole default bill history list
    const [currentDefaultBill, setCurrentDefaultBill] = useState<LandmanBillInfo>(undefined);
    const selectedPersonnel = useSelector((state: IRootState) => state.personnel.selectedPersonnel);

    const [billUnitProp, setBillUnitProp] = useState<string>(undefined); // unit information sent to update dialog
    const [billRateProp, setBillRateProp] = useState<number>(undefined); // rate information sent to update dialog

    const [projectBillHistory, setProjectBillHistory] = useState<Array<LandmanBillExceptionHistory>>(undefined); // bill exceptions for a specific project
    const [billExceptions, setBillExceptions] = useState<Array<LandmanBillException>>(undefined); // all bill exceptions

    const dispatch = useDispatch();

    // Initial render
    useEffect(() => {
        onCreate();

        return () => {
            onDestroy();
        };
    }, []);

    useEffect(() => {
        if (defaultBillHistory?.length > 0) {
            console.log('====> defaultBillHistory = ', defaultBillHistory);
            let currentDF = defaultBillHistory[0];

            if (currentDF) {
                setCurrentDefaultBill(currentDF);
            }
        } else {
            setCurrentDefaultBill(undefined);
        }
    }, [defaultBillHistory]);

    // resets props for update dialog when current bill rate is updated
    useEffect(() => {
        if (!!currentDefaultBill) {
            setValue('defaultBillRate', currentDefaultBill?.billRate ? parseFloat(currentDefaultBill?.billRate.toString()).toFixed(2) : 0.00);
            setBillRateProp(currentDefaultBill?.billRate ?? undefined);

            setValue('defaultBillUnit', currentDefaultBill?.billUnit ? currentDefaultBill?.billUnit : 'U');
            setBillUnitProp(currentDefaultBill?.billUnit ?? undefined);
        }
    }, [currentDefaultBill]);

    useEffect(() => {
        initializeData();
    }, [selectedPersonnel]);

    const onCreate = () => {
        console.log('BillingPage onCreate()');
        initializeData();
    };

    const onDestroy = () => {
        console.log('BillingPage onDestroy()');
    };

    const showToast = (message: string, color?: string, persistent?: boolean) => {
        setToast({ message, persistent, color });
    };

    // get all information on page for selected personnel
    const initializeData = () => {
        if (!!selectedPersonnel) {
            getAllBillExceptions();
            getDefaultBillHistory();
        }
    };

    // all bill exceptions
    const getAllBillExceptions = async () => {
        let result = await get(`v1.0/landmen/personnel/${selectedPersonnel?.tsdNo}/get-bill-exceptions`);

        if (response.ok) {
            setBillExceptions(result);
        } else {
            showToast('Unable to fetch personnel\'s bill exception information', `rgba(255,0,0,0.5)`);
        }
    };

    // bill exceptions for a selected project
    const getBillHistoryByProject = async (projectId: number) => {
        setIsLoading(true);
        if (selectedPersonnel?.tsdNo !== undefined && selectedPersonnel?.tsdNo !== null && selectedPersonnel?.tsdNo > 0) {
            let data = await get(`v1.0/landmen/personnel/${selectedPersonnel.tsdNo}/projects/${projectId}/get-project-bill-exceptions`);

            if (response.ok) {
                setProjectBillHistory(data);
                dispatch(actions.layout.showDialog(DialogList.SHOW_BILL_EXCEPTIONS));
            } else {
                showToast('Unable to get selected personnel\'s bill history information for this project', `rgba(255,0,0,0.5)`);
            }
        } else {
            showToast('Invalid employee number provided', `rgba(255,0,0,0.5)`);
        }

        setIsLoading(false);
    };

    const updateScreenBilling = () => {
        showToast('Bill rate update successful');
        getDefaultBillHistory();
    };

    // bill rate history
    const getDefaultBillHistory = async () => {
        setIsLoading(true);
        if (selectedPersonnel?.lmNo !== undefined && selectedPersonnel?.lmNo !== null && selectedPersonnel?.lmNo > 0) {
            let data = await get(`v1.0/landmen/personnel/${selectedPersonnel.lmNo}/get-bill-history`);

            if (response.ok) {
                setDefaultBillHistory(data);
            } else {
                showToast('Unable to get selected personnel\'s billing history information', `rgba(255,0,0,0.5)`);
            }
        } else {
            showToast('Invalid employee number provided', `rgba(255,0,0,0.5)`);
        }

        setIsLoading(false);
    };

    const displayTitle = () => {
        const title = selectedPersonnel ? `Billing Exceptions - ${selectedPersonnel?.akaName ? selectedPersonnel?.akaName : selectedPersonnel?.fName + ' ' + selectedPersonnel?.lName}` : 'Billing Exceptions';
        return title;
    };

    const showBillingPayHistory = async () => {
        if (!!selectedPersonnel) {
            await getDefaultBillHistory();
            dispatch(actions.layout.showDialog(DialogList.DEFAULT_BILL_HISTORY));
        } else if (!selectedPersonnel) {
            showToast('No personnel selected', `rgba(255,0,0,0.5)`);
        }
    };

    const showPersonnelData = () => {
        console.log('Display the Stuffs');
    };

    const toggleAdvancedQuickFind = () => {
        dispatch(actions.layout.showDialog(DialogList.PERSONNEL_ADVANCED_QUICK_FIND));
    };

    const showUpdateDialog = async () => {
        if (!!selectedPersonnel) {
            dispatch(actions.layout.showDialog(DialogList.UPDATE_BILLING_HISTORY));
        } else if (!selectedPersonnel) {
            showToast('No personnel selected', `rgba(255,0,0,0.5)`);
        }
    };

    const hideDialog = () => {
        dispatch(actions.layout.hideDialog());
    };

    /**
     * Sets the selected Personnel to the store if the given ID is valid and it's not already the same personnel
     * @param personnel - personnel payload
     */
    const setSelectedPersonnel = (personnel: TSDILandman) => {
        if (personnel) {
            personnel?.lmNo !== selectedPersonnel?.lmNo
                ? dispatch({ type: '[personnel] SET_SELECTED_PERSONNEL', payload: personnel })
                : showToast('Selected personnel already set', `rgba(78,117,154,0.5)`);

        } else {
            showToast('Selected personnel is undefined', `rgba(255,0,0,0.5)`);
        }
    };

    const displayBillingPayExceptions = () => {
        return <form id="personnelBillPayForm">
            <div>
                <table>
                    <thead>
                        <tr>
                            <th style={{ width: '4%' }}>Client</th>
                            <th style={{ width: '20%' }}>Client Name</th>
                            <th style={{ width: '4%' }}>Project</th>
                            <th style={{ width: '18%' }}>Project Name</th>
                            <th style={{ width: '10%' }}>Pay Rate</th>
                            <th style={{ width: '10%' }}>Pay Unit</th>
                            <th style={{ width: '10%' }}>Classification</th>
                        </tr>
                    </thead>
                    {billExceptions?.map((exception: LandmanBillException, index: number) => {
                        return (
                            <tbody key={`body-${index}`}>
                                <tr className="table-item-hover" style={{ padding: '1.5rem', textAlign: 'center' }}>
                                    <td style={{ width: '4%' }}>
                                        {exception.clNo}
                                    </td>
                                    <td style={{ width: '20%' }}>
                                        {exception.clientName}
                                    </td>
                                    <td style={{ width: '4%' }}>
                                        {exception.prNo}
                                    </td>
                                    <td style={{ width: '18%' }}>
                                        {exception.projectName}
                                    </td>
                                    <td style={{ width: '10%' }}>
                                        <RHFInputBox
                                            name={`filteredBillPay[${index}].billRate`}
                                            type="number"
                                            defaultVal={`${exception.billRate}`}
                                            register={register}
                                            errors={errors}
                                            style={{ width: '100%' }}
                                            bodyStyle={{ width: '100%' }}
                                            disabled={true}
                                        />
                                    </td>
                                    <td style={{ width: '10%' }}>
                                        <RHFSelect
                                            name={`filteredBillPay[${index}].billUnit`}
                                            defaultVal={exception.billUnit}
                                            options={[
                                                { value: 'D', label: 'Day' },
                                                { value: 'H', label: 'Hour' },
                                                { value: 'M', label: 'Month' },
                                            ]}
                                            register={register}
                                            errors={errors}
                                            style={{ width: '100%' }}
                                            bodyStyle={{ width: '100%' }}
                                            disabled={true}
                                        />
                                    </td>
                                    <td style={{ width: '10%' }}>
                                        <RHFInputBox
                                            name={`filteredBillPay[${index}].classification`}
                                            type="text"
                                            defaultVal={'TBD'}
                                            register={register}
                                            errors={errors}
                                            style={{ width: '100%' }}
                                            bodyStyle={{ width: '100%' }}
                                            disabled={true}
                                        />
                                    </td>
                                    <td style={{ width: '5%' }}>
                                        <Button
                                            className="blue-hover-button"
                                            style={{ width: '100%', height: '3rem' }}
                                            text={'History'}
                                            onClick={() => getBillHistoryByProject(exception.prId)}
                                        />
                                    </td>
                                </tr>
                            </tbody>
                        );
                    })}
                </table>
            </div>
        </form>;
    };

    return (
        <div>
            <PersonnelNavBar />
            <div>
                {toast && (
                    <PersonnelToast
                        message={toast.message}
                        color={toast.color}
                        onClose={() => setToast(null)}
                        persistent={toast.persistent}
                    />
                )}
            </div>
            <PersonnelSearch showPersonnelData={showPersonnelData} toggleAdvancedQuickFind={toggleAdvancedQuickFind} />
            <Page>
                <Page.Section width={12}>
                    <Panel backgroundColor="#D3D3D3">
                        <Panel>
                            <div className="form-row" style={{ textAlign: 'center', verticalAlign: 'middle', margin: '0' }}>
                                <RHFSelect
                                    name={`defaultBillUnit`}
                                    placeholder="Default Bill Unit"
                                    options={[
                                        { value: 'D', label: 'Day' },
                                        { value: 'H', label: 'Hour' },
                                        { value: 'M', label: 'Month' },
                                        { value: 'U', label: 'Unknown' }
                                    ]}
                                    style={{ width: '100%', border: '.5px solid LightGray', borderRadius: '5px' }}
                                    // onChange={() => filter()}
                                    bodyStyle={{ width: '25%' }}
                                    disabled={true}
                                    register={register}
                                    errors={errors}
                                    title="Default Bill Unit"
                                    label="Paid By"
                                    labelOnLeft={true}
                                />
                                <RHFInputBox
                                    name={`defaultBillRate`}
                                    placeholder="Default Bill Rate"
                                    type="number"
                                    style={{ width: '100%', border: '.5px solid LightGray', borderRadius: '5px' }}
                                    // onEnterPressed={(e) => e.key === 'Enter' ? filter() : {}}
                                    bodyStyle={{ width: '25%', marginLeft: '2rem' }}
                                    register={register}
                                    errors={errors}
                                    disabled={true}
                                    title="Default Bill Rate"
                                    label="Pay Rate"
                                    labelOnLeft={true}
                                />
                                <Button
                                    className="blue-hover-button"
                                    style={{ width: '10%', height: '3rem', marginLeft: '2rem', borderRadius: '5px' }}
                                    text={'Update'}
                                    intent="primary"
                                    onClick={() => showUpdateDialog()}
                                />
                                <Button
                                    className="blue-hover-button"
                                    style={{ width: '10%', height: '3rem', marginLeft: '3rem', borderRadius: '5px' }}
                                    text={'History'}
                                    onClick={() => showBillingPayHistory()}
                                />
                                <RHFSelect
                                    name={`display`}
                                    placeholder="Display Prospect Staffing"
                                    options={[
                                        { value: 'all', label: 'All' },
                                        { value: 'active', label: 'Active' },
                                        { value: 'inactive', label: 'Inactive' },
                                    ]}
                                    style={{ width: '100%', border: '.5px solid LightGray', borderRadius: '5px' }}
                                    // onChange={() => filter()}
                                    bodyStyle={{ width: '45%', marginLeft: '2rem' }}
                                    register={register}
                                    errors={errors}
                                />
                            </div>
                        </Panel>
                    </Panel>
                    <div style={{ height: '.1rem' }} />
                    <Panel backgroundColor="#D3D3D3">
                        <Panel panelTitle={displayTitle()}>
                            {billExceptions?.length > 0 && (
                                <div className="medium-panel-with-search" style={{ overflowY: 'scroll', padding: '2rem', paddingTop: '0' }}>
                                    {displayBillingPayExceptions()}
                                </div>)}
                            {isLoading && <LoadingSpinner />}
                            {!isLoading && !billExceptions?.length && (
                                <div style={{ textAlign: 'center', fontWeight: 'bold', fontSize: '20px', marginBottom: '3rem' }}>
                                    No Billing Exception Info
                                </div>)}
                        </Panel>
                    </Panel>
                </Page.Section>
            </Page>
            <DefaultBillHistoryDialog
                dialogName={DialogList.DEFAULT_BILL_HISTORY}
                hideDialog={hideDialog}
                defaultBillHistory={defaultBillHistory}
            />
            <UpdateBillHistoryDialog
                dialogName={DialogList.UPDATE_BILLING_HISTORY}
                hideDialog={hideDialog}
                currentRate={billRateProp ?? 0}
                currentUnit={billUnitProp ?? 'U'}
                selectedPersonnel={selectedPersonnel}
                updateScreenBilling={updateScreenBilling}
                showToast={showToast}
            />
            <BillExceptionHistoryDialog
                dialogName={DialogList.SHOW_BILL_EXCEPTIONS}
                defaultBillHistory={projectBillHistory}
                hideDialog={hideDialog}
            />
            <PersonnelAdvancedQuickFind
                dialogName={DialogList.PERSONNEL_ADVANCED_QUICK_FIND}
                hideDialog={hideDialog}
                selectedPersonnel={selectedPersonnel}
                selectPersonnel={setSelectedPersonnel}
            />
        </div>
    );
};

export default BillingPage;


// WEBPACK FOOTER //
// ./src/components/personnel/pages/BillingPage.tsx